import { useMemo, memo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import {
  GERENCIALES_DEBT_STATUS_OPTIONS,
  PIE_ALLOWED_COLORS,
} from '../../constants/gerencialesConstants';
import { formatCurrency } from '../../../../utils/formatUtils';

/**
 * Renderiza los gráficos de mora por estado y posesión.
 * @param {Object} props - Props del componente.
 * @param {Object} props.chartData - Datos de gráficos desde el backend.
 * @returns {JSX.Element} Secciones de gráficos.
 */
const DebtStatusCharts = ({ chartData }) => {
  const globalCounts = Array.isArray(chartData?.global_counts) ? chartData.global_counts : [];
  const groupedCounts = Array.isArray(chartData?.grouped_counts) ? chartData.grouped_counts : [];
  const totalAmounts = Array.isArray(chartData?.amounts_total) ? chartData.amounts_total : [];
  const paymentMethodStatus = Array.isArray(chartData?.payment_method_status)
    ? chartData.payment_method_status
    : [];

  const formatCount = (value) => {
    const numeric = Number(value ?? 0);
    if (Number.isNaN(numeric)) return '0';
    return numeric.toLocaleString('es-AR');
  };

  const pieData = useMemo(
    () =>
      globalCounts.map((row, index) => ({
        name: row.label,
        value: Number(row.count ?? 0),
        fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
      })),
    [globalCounts]
  );

  const groupedData = useMemo(
    () =>
      groupedCounts.map((row) => ({
        label: row.label,
        possessor_count: Number(row.possessor_count ?? 0),
        non_possessor_count: Number(row.non_possessor_count ?? 0),
      })),
    [groupedCounts]
  );

  const amountData = useMemo(
    () =>
      totalAmounts.map((row) => ({
        label: row.label,
        total_amount: Number(row.total_amount ?? 0),
      })),
    [totalAmounts]
  );

  const statusKeyMap = useMemo(() => {
    const map = {};
    GERENCIALES_DEBT_STATUS_OPTIONS.forEach((status) => {
      const key = status.toLowerCase().replace(/\s+/g, '_');
      map[status] = key;
    });
    return map;
  }, []);

  const paymentMethodData = useMemo(() => {
    const map = new Map();
    paymentMethodStatus.forEach((row) => {
      const method = row.payment_method || 'Sin forma de pago';
      const statusKey = statusKeyMap[row.debt_status] || row.debt_status;
      if (!map.has(method)) {
        map.set(method, { payment_method: method });
      }
      const entry = map.get(method);
      entry[statusKey] = (entry[statusKey] || 0) + Number(row.count ?? 0);
    });
    return Array.from(map.values());
  }, [paymentMethodStatus, statusKeyMap]);

  const ChartCard = ({ title, chartId, children }) => (
    <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
      <Box
        sx={{
          backgroundColor: '#3b3f46',
          color: 'white',
          px: 2.5,
          py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontSize: 30,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <Box id={chartId} sx={{ p: 2 }}>
        {children}
      </Box>
    </Paper>
  );

  return (
    <>
      <ChartCard title="Morosos por estado (Global)" chartId="gerenciales-debt-status-global">
        <Box sx={{ height: { xs: 280, md: 320 } }}>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={110}
                  isAnimationActive={false}
                  label={({ name, value }) => `${name}: ${formatCount(value)}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Morosos por estado (Poseedor vs No poseedor)"
        chartId="gerenciales-debt-status-grouped"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {groupedData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={groupedData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis tickFormatter={(value) => formatCount(value)} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                <Bar
                  dataKey="possessor_count"
                  name="Poseedor"
                  fill={PIE_ALLOWED_COLORS[0]}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="non_possessor_count"
                  name="No poseedor"
                  fill={PIE_ALLOWED_COLORS[4]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Importe total de mora por estado"
        chartId="gerenciales-debt-status-amounts"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {amountData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={amountData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <YAxis type="category" dataKey="label" width={130} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Bar
                  dataKey="total_amount"
                  name="Total mora"
                  fill={PIE_ALLOWED_COLORS[1]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard title="Estado de deuda por Forma de pago" chartId="gerenciales-debt-status-payment-method">
        <Box sx={{ height: { xs: 320, md: 380 } }}>
          {paymentMethodData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={paymentMethodData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => formatCount(value)} />
                <YAxis type="category" dataKey="payment_method" width={160} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                {GERENCIALES_DEBT_STATUS_OPTIONS.map((status, index) => (
                  <Bar
                    key={status}
                    dataKey={statusKeyMap[status]}
                    name={status}
                    stackId="debt-status"
                    fill={PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length]}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>
    </>
  );
};

export default memo(DebtStatusCharts);
